import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Col, Dropdown, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import useFetch from '../../../_fetch'
import { formatLocalDateForInput } from '../../../_utils'
import DateRangeSelector from '../home/dashboard-content/date-range-selector'

type ConversionStatus = 'pending' | 'approved' | 'rejected'

type ConversionRow = {
  id: number
  partnership_id: string
  publisher_user_id: string
  click_ref: string | null
  captured_at: string | null
  order_id: string
  amount_raw: string | null
  amount_value: number | null
  sale_percentage: number | null
  commission_value: number | null
  currency: string | null
  page_url: string | null
  ip_address: string | null
  user_agent: string | null
  origin: string | null
  referer: string | null
  event_timestamp: string | null
  received_at: string
  status: ConversionStatus
  is_valid: number
  validation_error: string | null
}

type ListResponse = {
  items: ConversionRow[]
  total: number
  limit: number
  offset: number
  from?: string
  to?: string
}

function toDateInputValue(date: Date) {
  return date.toISOString().slice(0, 10)
}

function defaultDateRange() {
  const to = new Date()
  const from = new Date()
  from.setDate(to.getDate() - 29)

  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  }
}

function formatDateTime(value?: string | null) {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat('da-DK', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function formatPercent(value?: number | null) {
  if (value == null || Number.isNaN(Number(value))) return '-'
  return `${Number(value)}%`
}

function formatMoney(value?: number | null, currency?: string | null) {
  if (value == null || Number.isNaN(Number(value))) return '-'
  const currencySuffix = currency ? ` ${currency}` : ''
  return `${Number(value)}${currencySuffix}`
}

export default function Conversions() {
  const { doFetch } = useFetch()
  const defaultRange = defaultDateRange()

  const [rows, setRows] = useState<ConversionRow[]>([])
  const [total, setTotal] = useState(0)
  const [limit, setLimit] = useState(100)
  const [offset, setOffset] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [validityFilter, setValidityFilter] = useState<string>('valid')
  const [search, setSearch] = useState('')
  const [draftStatusFilter, setDraftStatusFilter] = useState<string>('')
  const [draftValidityFilter, setDraftValidityFilter] = useState<string>('valid')
  const [draftSearch, setDraftSearch] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [fromDate, setFromDate] = useState(defaultRange.from)
  const [toDate, setToDate] = useState(defaultRange.to)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [detailsRow, setDetailsRow] = useState<ConversionRow | null>(null)
  const [detailsStatus, setDetailsStatus] = useState<ConversionStatus>('pending')
  const [savingStatusId, setSavingStatusId] = useState<number | null>(null)

  const currentPage = Math.floor(offset / limit) + 1
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const load = async (opts?: {
    nextOffset?: number
    nextLimit?: number
    nextStatus?: string
    nextValidity?: string
    nextSearch?: string
    nextFromDate?: string
    nextToDate?: string
  }) => {
    const targetOffset = opts?.nextOffset ?? offset
    const targetLimit = opts?.nextLimit ?? limit
    const targetStatus = opts?.nextStatus ?? statusFilter
    const targetValidity = opts?.nextValidity ?? validityFilter
    const targetSearch = opts?.nextSearch ?? search
    const targetFromDate = opts?.nextFromDate ?? fromDate
    const targetToDate = opts?.nextToDate ?? toDate

    setLoading(true)
    setError('')

    try {
      const qs = new URLSearchParams()
      qs.set('limit', String(targetLimit))
      qs.set('offset', String(targetOffset))
      if (targetStatus) qs.set('status', targetStatus)
      if (targetValidity) qs.set('validity', targetValidity)
      if (targetSearch.trim()) qs.set('search', targetSearch.trim())
      if (targetFromDate) qs.set('from', targetFromDate)
      if (targetToDate) qs.set('to', targetToDate)

      const res = await doFetch(`/user/admin/conversions?${qs.toString()}`)
      if (res.status >= 400) throw await res.text()

      const data: ListResponse = await res.json()
      setRows(Array.isArray(data.items) ? data.items : [])
      setTotal(Number(data.total || 0))
      setLimit(Number(data.limit || targetLimit))
      setOffset(Number(data.offset || targetOffset))
    } catch (err: any) {
      setError(typeof err === 'string' ? err : 'Could not load conversions')
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onOpenFilters = (nextOpen: boolean) => {
    setFiltersOpen(nextOpen)
    if (nextOpen) {
      setDraftStatusFilter(statusFilter)
      setDraftValidityFilter(validityFilter)
      setDraftSearch(search)
    }
  }

  const onApplyFilters = () => {
    setStatusFilter(draftStatusFilter)
    setValidityFilter(draftValidityFilter)
    setSearch(draftSearch)
    load({
      nextOffset: 0,
      nextStatus: draftStatusFilter,
      nextValidity: draftValidityFilter,
      nextSearch: draftSearch,
    })
    setFiltersOpen(false)
  }

  const onClearFilters = () => {
    setDraftStatusFilter('')
    setDraftValidityFilter('valid')
    setDraftSearch('')
    setStatusFilter('')
    setValidityFilter('valid')
    setSearch('')
    load({ nextOffset: 0, nextStatus: '', nextValidity: 'valid', nextSearch: '' })
    setFiltersOpen(false)
  }

  const goToPage = (page: number) => {
    const clamped = Math.min(Math.max(page, 1), totalPages)
    const nextOffset = (clamped - 1) * limit
    load({ nextOffset })
  }

  const updateStatus = async (id: number, status: ConversionStatus) => {
    const row = rows.find((item) => item.id === id)
    if (!row) return
    if (status === row.status) return

    setSavingStatusId(id)
    setError('')

    try {
      const res = await doFetch(`/user/admin/conversions/${id}/status`, { status }, 'PATCH')
      if (res.status >= 400) throw await res.text()

      const updated: ConversionRow = await res.json()

      setRows((prev) => prev.map((row) => (row.id === id ? updated : row)))
      setDetailsRow((prev) => (prev && prev.id === id ? updated : prev))
    } catch (err: any) {
      setError(typeof err === 'string' ? err : 'Could not update status')
    } finally {
      setSavingStatusId(null)
    }
  }

  const pageRows = useMemo(() => rows, [rows])
  const pendingCount = rows.filter((row) => row.status === 'pending').length
  const approvedCount = rows.filter((row) => row.status === 'approved').length
  const rejectedCount = rows.filter((row) => row.status === 'rejected').length
  const validCount = rows.filter((row) => row.is_valid).length
  const invalidCount = rows.filter((row) => !row.is_valid).length
  const isFilterActive = Boolean(statusFilter || validityFilter || search.trim())

  return (
    <div className="pe-2">
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Conversions</h5>
              <div className="text-muted small">Default range is the last 30 days.</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <DateRangeSelector
                pageKey="conversions"
                onSubmit={async (startDate, endDate) => {
                  const nextFromDate = formatLocalDateForInput(startDate)
                  const nextToDate = formatLocalDateForInput(endDate)

                  setFromDate(nextFromDate)
                  setToDate(nextToDate)
                  await load({ nextOffset: 0, nextFromDate, nextToDate })
                }}
              />

              <Dropdown show={filtersOpen} onToggle={(open) => onOpenFilters(Boolean(open))}>
                <Dropdown.Toggle variant={isFilterActive ? 'primary' : 'outline-secondary'} size="sm">
                  Filters{isFilterActive ? ' ●' : ''}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end" className="p-3" style={{ minWidth: '320px' }}>
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1">Status</Form.Label>
                    <div className="d-flex gap-1 flex-wrap">
                      <Button size="sm" variant={draftStatusFilter === '' ? 'dark' : 'outline-secondary'} onClick={() => setDraftStatusFilter('')}>All</Button>
                      <Button size="sm" variant={draftStatusFilter === 'pending' ? 'dark' : 'outline-secondary'} onClick={() => setDraftStatusFilter('pending')}>Pending</Button>
                      <Button size="sm" variant={draftStatusFilter === 'approved' ? 'dark' : 'outline-secondary'} onClick={() => setDraftStatusFilter('approved')}>Approved</Button>
                      <Button size="sm" variant={draftStatusFilter === 'rejected' ? 'dark' : 'outline-secondary'} onClick={() => setDraftStatusFilter('rejected')}>Rejected</Button>
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1">Validity</Form.Label>
                    <div className="d-flex gap-1 flex-wrap">
                      <Button size="sm" variant={draftValidityFilter === 'valid' ? 'dark' : 'outline-secondary'} onClick={() => setDraftValidityFilter('valid')}>Valid</Button>
                      <Button size="sm" variant={draftValidityFilter === 'invalid' ? 'dark' : 'outline-secondary'} onClick={() => setDraftValidityFilter('invalid')}>Invalid</Button>
                      <Button size="sm" variant={draftValidityFilter === '' ? 'dark' : 'outline-secondary'} onClick={() => setDraftValidityFilter('')}>All</Button>
                    </div>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="mb-1">Search</Form.Label>
                    <Form.Control
                      value={draftSearch}
                      onChange={(e) => setDraftSearch(e.target.value)}
                      placeholder="order id, partnership id, publisher id, click ref"
                    />
                  </Form.Group>
                  <div className="mt-3 d-flex justify-content-between">
                    <Button variant="outline-secondary" size="sm" onClick={onClearFilters}>
                      Clear
                    </Button>
                    <Button size="sm" onClick={onApplyFilters}>
                      Apply
                    </Button>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <div className="small text-muted pb-2">Showing {total} total conversions for selected date range.</div>

          <div className="d-flex gap-2" style={{ overflowX: 'auto' }}>
            <div style={{ flex: '1 0 0', minWidth: '130px' }}>
              <Card className="h-100 border-0 bg-light">
                <Card.Body className="py-2 px-3">
                  <div className="small text-muted">Pending</div>
                  <div className="fw-semibold">{pendingCount}</div>
                </Card.Body>
              </Card>
            </div>
            <div style={{ flex: '1 0 0', minWidth: '130px' }}>
              <Card className="h-100 border-0 bg-light">
                <Card.Body className="py-2 px-3">
                  <div className="small text-muted">Approved</div>
                  <div className="fw-semibold">{approvedCount}</div>
                </Card.Body>
              </Card>
            </div>
            <div style={{ flex: '1 0 0', minWidth: '130px' }}>
              <Card className="h-100 border-0 bg-light">
                <Card.Body className="py-2 px-3">
                  <div className="small text-muted">Rejected</div>
                  <div className="fw-semibold">{rejectedCount}</div>
                </Card.Body>
              </Card>
            </div>
            <div style={{ flex: '1 0 0', minWidth: '130px' }}>
              <Card className="h-100 border-0 bg-light">
                <Card.Body className="py-2 px-3">
                  <div className="small text-muted">Valid</div>
                  <div className="fw-semibold">{validCount}</div>
                </Card.Body>
              </Card>
            </div>
            <div style={{ flex: '1 0 0', minWidth: '130px' }}>
              <Card className="h-100 border-0 bg-light">
                <Card.Body className="py-2 px-3">
                  <div className="small text-muted">Invalid</div>
                  <div className="fw-semibold">{invalidCount}</div>
                </Card.Body>
              </Card>
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted small">Total: {total}</div>
                <div className="text-muted small">Page {currentPage} / {totalPages}</div>
              </div>
              <Table hover responsive size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Received</th>
                    <th>Order</th>
                    <th>Partnership</th>
                    <th>Publisher</th>
                    <th>Amount</th>
                    <th>Pct</th>
                    <th>Commission</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-4">No conversions found</td>
                    </tr>
                  ) : (
                    pageRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{formatDateTime(row.received_at)}</td>
                        <td>{row.order_id || '-'}</td>
                        <td>{row.partnership_id || '-'}</td>
                        <td>{row.publisher_user_id || '-'}</td>
                        <td>{row.amount_value != null ? formatMoney(row.amount_value, row.currency) : (row.amount_raw || '-')}</td>
                        <td>{formatPercent(row.sale_percentage)}</td>
                        <td>{formatMoney(row.commission_value, row.currency)}</td>
                        <td>
                          {row.status}
                        </td>
                        <td>
                          <Button
                            variant="outline-dark"
                            size="sm"
                            onClick={() => {
                              setDetailsRow(row)
                              setDetailsStatus(row.status)
                            }}
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">Rows per page</span>
                  <Form.Select
                    size="sm"
                    style={{ width: '110px' }}
                    value={String(limit)}
                    onChange={(e) => {
                      const nextLimit = Number(e.target.value) || 100
                      load({ nextOffset: 0, nextLimit })
                    }}
                  >
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="500">500</option>
                  </Form.Select>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                    Prev
                  </Button>
                  <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!detailsRow} onHide={() => setDetailsRow(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Conversion details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!detailsRow ? null : <>
            <div className="small">
              {Object.entries(detailsRow).map(([key, value]) => (
                <Row key={key} className="py-1 border-bottom">
                  <Col md={4} className="text-muted">{key}</Col>
                  <Col md={8} style={{ wordBreak: 'break-word' }}>
                    {key === 'status' && value
                      ? <>
                          <div style={{ maxWidth: '280px' }}>
                            <Form.Select
                                size="sm"
                                value={detailsStatus}
                                disabled={savingStatusId === detailsRow.id}
                                onChange={async (e) => {
                                  const nextStatus = e.target.value as ConversionStatus
                                  setDetailsStatus(nextStatus)
                                  await updateStatus(detailsRow.id, nextStatus)
                                }}
                              >
                                <option value="pending">pending</option>
                                <option value="approved">approved</option>
                                <option value="rejected">rejected</option>
                            </Form.Select>
                          </div>
                        </>
                      : key === 'sale_percentage'
                        ? formatPercent(typeof value === 'number' ? value : Number(value))
                      : key === 'commission_value'
                        ? formatMoney(typeof value === 'number' ? value : Number(value), detailsRow.currency)
                      : (value == null || value === '' ? '-' : String(value))}
                  </Col>
                </Row>
              ))}
            </div>
          </>}
        </Modal.Body>
      </Modal>
    </div>
  )
}
